import { MdClose, MdTune, MdExpandMore, MdExpandLess } from 'react-icons/md';

/**
 * Shared sticky toolbar for results pages (landing + dashboard/jobs).
 * Layout:
 * - Left: "Prikazano X rezultata" (+ optional "za Y") — jedan broj, bez redundantnog "u mapi"
 * - Center: sort dropdown
 * - Right: Filters + view toggle + optional close
 * Mobile: naturally wraps into 2 rows via flex-wrap.
 * Sticky bar ima pozadinu da sadržaj iza ne proviri pri skrolu.
 */
export default function ResultsToolbar({
  count = 0,
  mapCount = null,
  totalCount,
  locationLabel,
  sortBy,
  onSortChange,
  onOpenFilters,
  filtersOpen,
  viewMode,
  onViewModeChange,
  onClose,
  onClear,
  stickyTopClass = 'top-[4.5rem]',
}) {
  const displayCount = totalCount != null ? totalCount : count;
  return (
    <div
      className={[
        'sticky z-30',
        stickyTopClass,
        'flex flex-wrap items-center justify-between gap-3',
        'py-3 px-4 sm:px-5',
        'rounded-2xl',
        'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md',
        'border border-gray-200/80 dark:border-gray-700/80',
        'shadow-sm',
      ].join(' ')}
    >
      {/* LEFT — Prikazano X rezultata (za Y) — bez redundantnog "u mapi" */}
      <div className="flex items-center gap-2 flex-wrap min-w-0">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Prikazano <span className="font-semibold text-gray-900 dark:text-gray-100">{displayCount}</span>
          {displayCount === 1 ? ' rezultat' : ' rezultata'}
          {locationLabel && (
            <span className="text-gray-600 dark:text-gray-400"> za &quot;{locationLabel}&quot;</span>
          )}
        </span>
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Ukloni rezultate"
          >
            <MdClose className="text-base" /> Ukloni rezultate
          </button>
        )}
      </div>

      {/* RIGHT — sort, filteri, mapa/lista */}
      <div className="flex items-center gap-2 sm:gap-3 order-2 flex-wrap">
        <select
          value={sortBy}
          onChange={(e) => onSortChange?.(e.target.value)}
          className="text-sm border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          aria-label="Sortiraj rezultate"
        >
          <option value="default">Podrazumevano</option>
          <option value="price_asc">Cena: niža → viša</option>
          <option value="price_desc">Cena: viša → niža</option>
          <option value="rating">Ocena</option>
        </select>

        <button
          type="button"
          onClick={onOpenFilters}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-primary-500 whitespace-nowrap"
          aria-label="Filteri"
        >
          <MdTune className="text-lg" /> Filteri
          {typeof filtersOpen === 'boolean' && (
            filtersOpen ? <MdExpandLess className="text-lg opacity-80" /> : <MdExpandMore className="text-lg opacity-80" />
          )}
        </button>

        {/* Mobile-only view toggle (desktop shows both list + map) */}
        <div className="lg:hidden inline-flex rounded-full border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 p-1">
          <button
            type="button"
            onClick={() => onViewModeChange?.('map')}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${
              viewMode === 'map'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            Mapa
          </button>
          <button
            type="button"
            onClick={() => onViewModeChange?.('list')}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${
              viewMode === 'list'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            Lista
          </button>
        </div>

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Zatvori pretragu"
          >
            <MdClose className="text-xl" />
          </button>
        )}
      </div>
    </div>
  );
}

